import React, { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, ShieldCheck, Globe } from "lucide-react";
import { useApp } from "../context/AppContext";
import { LANGUAGES } from "../i18n/translations";
import { Button } from "./ui/button";
import { HeaderActions } from "./HeaderActions";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/services", key: "nav.services" },
  { to: "/coverage", key: "nav.coverage" },
  { to: "/offers", key: "nav.offers" },
  { to: "/about", key: "nav.about" },
  { to: "/support", key: "nav.support" },
];

function LanguageSwitcher({ mobile }) {
  const { lang, setLang, t } = useApp();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  return (
    <div className={`relative ${mobile ? "" : ""}`}>
      <button data-testid={mobile ? "mobile-language-switcher" : "language-switcher"} aria-expanded={open} onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-neutral-700 hover:bg-black/[0.04] transition-colors">
        <Globe className="w-4 h-4 text-orange-500" /> {current.native} <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div data-testid={`${mobile ? "mobile-" : ""}language-dismiss`} className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid={`${mobile ? "mobile-" : ""}language-menu`} className="absolute right-0 mt-1 z-50 w-44 bg-white rounded-xl shadow-xl border border-black/10 py-1.5">
            <p className="px-3 py-1 text-[11px] uppercase tracking-wider text-neutral-400">{t("common.language")}</p>
            {LANGUAGES.map((l) => (
              <button key={l.code} data-testid={`${mobile ? "mobile-" : ""}lang-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-black/[0.04] ${l.code === lang ? "text-orange-500 font-semibold" : "text-neutral-700"}`}>
                <span>{l.native}</span>
                <span className="text-xs text-neutral-400">{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout, t } = useApp();
  const loc = useLocation();
  const nav = useNavigate();
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty("--site-header-height", `${entry.target.getBoundingClientRect().height}px`);
    });
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "employee" ? "/staff" : "/dashboard";

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <div ref={headerRef} className="glass border-b border-black/10">
        <div className="max-w-[1680px] mx-auto px-5 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-5">
          <Link data-testid="navbar-home-logo" to="/" className="flex items-center group shrink-0">
            <img data-testid="header-logo" src="/smartlogistics-full-logo.png" alt="SmartLogistics" className="h-12 sm:h-14 w-auto max-w-[170px] sm:max-w-[230px] object-contain" />
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`navbar-${n.key.replace("nav.", "")}-link`}
                className={`px-2 py-2 text-sm font-medium rounded-lg transition-colors ${
                  loc.pathname === n.to ? "text-orange-500 bg-black/[0.03]" : "text-neutral-700 hover:text-neutral-800 hover:bg-black/[0.04]"
                }`}
              >
                {t(n.key)}
              </Link>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            {user ? (
              <div className="flex items-center gap-2">
                <Button data-testid="navbar-dashboard-button" variant="ghost" className="text-neutral-800 hover:text-neutral-900 gap-2" onClick={() => nav(dashPath)}>
                  {user.role === "admin" ? <ShieldCheck className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}
                  {user.role === "admin" ? "Admin" : user.role === "employee" ? "Staff" : "Dashboard"}
                </Button>
                <Button data-testid="navbar-logout-button" aria-label="Log out" variant="ghost" size="icon" className="text-neutral-600 hover:text-orange-500" onClick={() => { logout(); nav("/"); }}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button data-testid="navbar-login-button" variant="ghost" className="text-neutral-800 hover:text-neutral-900" onClick={() => nav("/login")}>{t("nav.login")}</Button>
              </>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-2 sm:gap-4 min-w-0">
            <button data-testid="navbar-mobile-menu-button" aria-label="Menu" aria-expanded={open} className="text-neutral-900 shrink-0 p-1" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
          </div>
        </div>
        <nav data-testid="mobile-public-navigation" aria-label="Main pages" className="xl:hidden grid grid-cols-3 sm:grid-cols-6 gap-1 px-3 pb-2">
          {NAV.map((n) => <Link key={n.to} data-testid={`compact-${n.key.replace("nav.", "")}-link`} to={n.to} className={`min-w-0 text-center rounded-lg px-2 py-2 text-xs sm:text-sm leading-snug break-words transition-colors ${loc.pathname === n.to ? "text-orange-600 bg-orange-50" : "text-neutral-700 hover:bg-black/[0.04]"}`}>{t(n.key)}</Link>)}
        </nav>
        <HeaderActions />
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" style={{ maxHeight: "calc(100dvh - var(--site-header-height, 148px))" }} className="xl:hidden glass border-b border-black/10 px-5 py-4 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <Link data-testid={`mobile-${n.key.replace("nav.", "")}-link`} key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="block px-4 py-3 rounded-lg text-neutral-800 hover:bg-black/[0.04]">{t(n.key)}</Link>
          ))}
          <div className="pt-2 border-t border-black/10 mt-2"><LanguageSwitcher mobile /></div>
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Button data-testid="mobile-dashboard-button" variant="outline" className="w-full border-neutral-300" onClick={() => { nav(dashPath); setOpen(false); }}>My Panel</Button>
                <Button data-testid="mobile-logout-button" variant="ghost" className="w-full" onClick={() => { logout(); nav("/"); setOpen(false); }}>Log out</Button>
              </>
            ) : (
              <Button data-testid="mobile-login-button" variant="outline" className="w-full border-neutral-300" onClick={() => { nav("/login"); setOpen(false); }}>{t("nav.login")}</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
